import { parseDateValue } from '@ben/common/date';
import Checkbox from '@ben/orion/checkbox';
import DateInput from '@ben/orion/date-input';
import Button, { ButtonVariant } from '@ben/orion/deprecated-button';
import MantineModal from '@ben/orion/mantine-modal';
import RadioButton from '@ben/orion/radio-button';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { memo, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import styled, { css } from 'styled-components';
import { fieldCannotBeBlankErrorMessage } from '../../shared/constants';

interface IValidationDialogProps {
  opened: boolean;
  onClose: () => void;
  columnHeaders: string[];
  fileData: Record<string, string | number | boolean>[];
  dataType: Record<string, string>;
  setDataType: (dataType: Record<string, string>) => void;
  setIsValidated: (hasValidated: boolean) => void;
  setIsFallback: (isFallback: boolean) => void;
  isFallback: boolean;
  setEffectiveFrom: (effectiveFrom?: string) => void;
  effectiveFrom: string | undefined;
}

const ValidationDialog = ({
  opened,
  onClose,
  columnHeaders,
  fileData,
  dataType,
  setDataType,
  setIsValidated,
  setIsFallback,
  isFallback,
  setEffectiveFrom,
  effectiveFrom,
}: IValidationDialogProps) => {
  const [effectiveFromHasError, setEffectiveFromHasError] = useState(false);

  const validateData = useCallback(() => {
    if (!effectiveFrom) {
      setEffectiveFromHasError(true);
      return;
    }

    onClose();

    for (const columnHeader of columnHeaders) {
      const columnDataType = dataType[columnHeader];
      if (columnDataType === 'number') {
        for (const row of fileData) {
          const value = row[columnHeader];
          if (Number.isNaN(Number(value))) {
            toast.error(
              `Invalid value for column ${columnHeader} at row id ${row['id']}: ${value}`
            );

            setIsValidated(false);
            return;
          }
        }
      }
    }

    setIsValidated(true);
  }, [
    columnHeaders,
    dataType,
    onClose,
    effectiveFrom,
    fileData,
    setIsValidated,
  ]);

  return (
    <MantineModal opened={opened} onClose={onClose}>
      <DialogContent>
        <TextContainer>
          <Typography variant={TypographyVariant.h2}>Set data type</Typography>
          <Typography variant={TypographyVariant.bodyLg1}>
            Before the rate table is uploaded, set the data type of each column
          </Typography>
        </TextContainer>
        <ContentContainer>
          <DateInput
            label="Effective from"
            width="100%"
            onChange={(date) =>
              setEffectiveFrom(
                date ? date.toISOString().replace(/\.(\d{3})Z$/, '.$1000Z') : ''
              )
            }
            value={effectiveFrom ? parseDateValue(effectiveFrom) : null}
            errorMessage={
              effectiveFromHasError && !effectiveFrom
                ? fieldCannotBeBlankErrorMessage
                : undefined
            }
          />

          <Checkbox
            label="Fallback table"
            onChange={setIsFallback}
            value={isFallback}
          />

          <Divider />

          <ColumnsContainer>
            {columnHeaders?.map((columnHeader) => (
              <ColumnWithOptionContainer key={columnHeader}>
                <Typography variant={TypographyVariant.bodyLg2}>
                  {columnHeader}
                </Typography>
                <OptionsContainer>
                  <RadioButton
                    name={columnHeader}
                    label="String"
                    checked={dataType[columnHeader] === 'string'}
                    onChange={() => {
                      setDataType({
                        ...dataType,
                        [columnHeader]: 'string',
                      });
                    }}
                    disabled={columnHeader === 'rate'}
                  />
                  <RadioButton
                    name={columnHeader}
                    label="Number"
                    checked={dataType[columnHeader] === 'number'}
                    onChange={() => {
                      setDataType({
                        ...dataType,
                        [columnHeader]: 'number',
                      });
                    }}
                    disabled={columnHeader === 'rate'}
                  />
                </OptionsContainer>
              </ColumnWithOptionContainer>
            ))}
          </ColumnsContainer>
        </ContentContainer>

        <DialogButtonContainer>
          <Button
            label="Cancel"
            variant={ButtonVariant.secondary}
            onClick={() => {
              setEffectiveFromHasError(false);
              onClose();
            }}
            width="208px"
          />
          <Button
            label="Continue"
            variant={ButtonVariant.primary}
            onClick={validateData}
            width="208px"
          />
        </DialogButtonContainer>
      </DialogContent>
    </MantineModal>
  );
};

export default memo(ValidationDialog);

const DialogContent = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.palette.neutral['00']};
    border-radius: ${theme.borderRadius.sm};
    padding: ${theme.spacing(5)};
    width: 520px;
    height: fit-content;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `}
`;

const TextContainer = styled.div`
  text-align: center;
`;

const ColumnsContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `}
`;

const ColumnWithOptionContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const OptionsContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(3)};
  `}
`;

const DialogButtonContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-top: ${theme.spacing(5)};
  `}
`;

const Divider = styled.div`
  ${({ theme }) => css`
    width: 100%;
    height: 1px;
    border-top: 1px dashed ${theme.palette.neutral[60]};
  `}
`;

const ContentContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.spacing(2.5)};
    flex-direction: column;
  `}
`;
